import React, { memo, useState } from "react";
import "./timeline-editor.css";

const clock = (t) => new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const stamp = (t) => new Date(t).toLocaleString([], { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

const TYPE_WORDS = { buyin: "seat", rebuy: "rebuy", out: "bust", back: "back in" };

const HEADLINES = {
  live_started: "Started the live game",
  live_discarded: "Discarded the live game",
  game_saved_from_live: "Saved the game to the ledger",
  game_created: "Added the game",
  game_updated: "Edited the game",
  game_deleted: "Deleted the game",
};

const PLACE_FIELDS = {
  firstWinnerId: "1st place",
  secondWinnerId: "2nd place",
  thirdWinnerId: "3rd place",
  splitWinnerId: "Tied 1st place",
  jackpotWinnerId: "Jackpot winner",
};

// One change as a line of text. warn marks buy-ins changed without a matching
// timeline entry: the case a moderator wants to spot.
function describe(c, getName, money) {
  const name = (pid) => getName(pid) || "Unknown player";
  switch (c.k) {
    case "event_added":
      if (c.type === "rebuy") return { text: `Added a rebuy for ${name(c.pid)} at ${clock(c.t)}` };
      if (c.type === "buyin") return { text: `Seated ${name(c.pid)} at ${clock(c.t)}` };
      if (c.type === "out") return { text: `Marked ${name(c.pid)} out at ${clock(c.t)}` };
      if (c.type === "back") return { text: `Brought ${name(c.pid)} back in at ${clock(c.t)}` };
      return { text: `Added ${c.type} for ${name(c.pid)}` };
    case "event_removed":
      return { text: `Removed ${name(c.pid)}'s ${TYPE_WORDS[c.type] || c.type} (${clock(c.t)})` };
    case "event_moved":
      return { text: `Moved ${name(c.pid)}'s ${TYPE_WORDS[c.type] || c.type} from ${clock(c.from)} to ${clock(c.to)}` };
    case "buyin":
      return c.unit === "count"
        ? { text: `Changed ${name(c.pid)}'s buy-ins from ${c.from} to ${c.to} with no timeline entry`, warn: true }
        : { text: `Changed ${name(c.pid)}'s buy-in from ${money(c.from)} to ${money(c.to)}`, warn: true };
    case "player_added":
      return { text: c.amount ? `Added ${name(c.pid)} with ${money(c.amount)}` : `Added ${name(c.pid)} to the game` };
    case "player_removed":
      return { text: `Removed ${name(c.pid)} from the game`, warn: true };
    case "players":
      return { text: `Players: ${(c.pids || []).map(name).join(", ") || "none"}` };
    case "start":
      return { text: c.from ? `Start time ${clock(c.from)} → ${clock(c.to)}` : `Start time ${clock(c.to)}` };
    case "end":
      return { text: c.from ? `End time ${clock(c.from)} → ${clock(c.to)}` : `End time ${clock(c.to)}` };
    case "paused":
      return { text: "Paused the game" };
    case "resumed":
      return { text: "Resumed the game" };
    case "chips":
      return { text: `${name(c.pid)}'s chip count: ${c.from ?? "none"} → ${c.to ?? "none"}` };
    case "field": {
      if (PLACE_FIELDS[c.field]) return { text: `${PLACE_FIELDS[c.field]}: ${c.from ? name(c.from) : "none"} → ${c.to ? name(c.to) : "none"}` };
      if (c.field === "isOut") return { text: `Marked ${name(c.pid)} ${c.to ? "out" : "back in"} with no timeline entry`, warn: true };
      if (c.field === "buyInAmount") return c.from == null ? { text: `Buy-in ${money(c.to)}` } : { text: `Buy-in amount ${money(c.from)} → ${money(c.to)}`, warn: true };
      if (c.field === "jackpotCut") return { text: `Jackpot cut ${money(c.from || 0)} → ${money(c.to || 0)}` };
      if (c.field === "jackpot") return { text: c.to ? `Jackpot: ${name(c.to.winnerId)}${c.to.amount ? `, ${money(c.to.amount)}` : ""}` : "Removed the jackpot" };
      if (c.field === "jackpotCards") return { text: `Jackpot cards: ${c.to || "none"}` };
      if (c.field === "notes") return { text: "Changed the notes" };
      if (c.field === "date") return { text: `Date ${c.from || "none"} → ${c.to || "none"}` };
      if (c.field === "location") return { text: `Location ${c.from || "none"} → ${c.to || "none"}` };
      return { text: `${c.field} changed` };
    }
    default:
      return { text: "Changed the game" };
  }
}

const LOG_PREVIEW = 6;

// The session change log: newest first, who did what and when. Entries come
// from the server (groups/{gid}/sessionLog), so the app can't leave any out.
export const SessionLog = memo(function SessionLog({ entries, getName, formatMoney, emptyText = "No changes logged yet." }) {
  const [showAll, setShowAll] = useState(false);
  if (!entries.length) return <div className="session-log-empty">{emptyText}</div>;
  const shown = showAll ? entries : entries.slice(0, LOG_PREVIEW);
  return (
    <div className="session-log">
      {shown.map((entry) => {
        const lines = (entry.changes || []).map((c) => describe(c, getName, formatMoney));
        const headline = HEADLINES[entry.action];
        return (
          <div key={entry.id} className="session-log-entry">
            <div className="session-log-meta">
              <span className="session-log-actor">{entry.actorName || "Unknown"}</span>
              <span className="session-log-time">{entry.atMs ? stamp(entry.atMs) : ""}</span>
            </div>
            {headline && <div className="session-log-line session-log-headline">{headline}</div>}
            {lines.map((line, i) => (
              <div key={i} className={`session-log-line${line.warn ? " session-log-line--warn" : ""}`}>{line.text}</div>
            ))}
            {entry.reason && <div className="session-log-reason">Reason: {entry.reason}</div>}
          </div>
        );
      })}
      {entries.length > LOG_PREVIEW && (
        <button type="button" className="session-log-more" onClick={() => setShowAll((v) => !v)}>
          {showAll ? "Show fewer" : `Show all ${entries.length} changes`}
        </button>
      )}
    </div>
  );
});
